import React from 'react';
import { usePlaybook } from '../../context/PlaybookContext';
import { useCRM } from '../../context/CRMContext';
import SafeIcon from '../../common/SafeIcon';

const OnboardingManager = ({ projectId }) => {
  const { onboardingTasks, createStandardOnboardingPack, updateOnboardingTask } = usePlaybook();
  const { currentUser } = useCRM();

  const tasks = onboardingTasks.filter(t => t.project_id === projectId);
  const isCustomer = currentUser.role === 'Customer_Viewer';

  const handleStatusChange = (taskId, newStatus, submission = '') => {
    const updates = { status: newStatus };
    if (submission) updates.submission = submission;
    updateOnboardingTask(taskId, updates);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Godkjent': return 'bg-emerald-100 text-emerald-800 border-emerald-200';
      case 'Levert': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'Pågår': return 'bg-amber-100 text-amber-800 border-amber-200';
      default: return 'bg-slate-100 text-slate-500 border-slate-200';
    }
  };

  return (
    <div className="space-y-8 animate-fade-in">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-xl font-black text-slate-900">Onboarding</h3>
          <p className="text-sm text-slate-500">Oppgaver som må løses for å komme i gang.</p>
        </div>
        {!isCustomer && tasks.length === 0 && (
          <button 
            onClick={() => createStandardOnboardingPack(projectId)}
            className="flex items-center gap-2 px-6 py-3 bg-slate-900 text-white rounded-xl font-black text-xs uppercase tracking-widest hover:bg-slate-800 transition-all shadow-lg"
          >
            <SafeIcon icon="📦" /> Opprett standard-løype
          </button>
        )}
      </div>

      {tasks.length > 0 ? (
        <div className="grid grid-cols-1 gap-4">
          {tasks.map(task => (
            <div key={task.id} className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-all">
              <div className="flex justify-between items-start mb-4">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${task.owner === 'Kunde' ? 'bg-purple-50 text-purple-600' : 'bg-blue-50 text-blue-600'}`}>
                    <SafeIcon icon={task.owner === 'Kunde' ? '👤' : '🏢'} className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-900">{task.title}</h4>
                    <p className="text-xs text-slate-500">{task.description}</p>
                  </div>
                </div>
                <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border ${getStatusColor(task.status)}`}>
                  {task.status}
                </span>
              </div>

              {/* Interaction Area */}
              <div className="bg-slate-50 p-4 rounded-xl border border-slate-100 mt-4">
                {task.status === 'Godkjent' ? (
                  <div className="flex items-center gap-2 text-emerald-600 text-sm font-bold">
                    <SafeIcon icon="✅" /> Oppgaven er godkjent og låst.
                  </div>
                ) : (
                  <div className="space-y-3">
                    {/* Customer Submission View */}
                    {(isCustomer && task.owner === 'Kunde') && (
                      <div className="flex gap-3">
                        <input 
                          disabled={task.status === 'Levert'}
                          placeholder="Skriv ditt svar her..." 
                          className="flex-1 px-4 py-2 border border-slate-300 rounded-lg text-sm outline-none focus:border-purple-500"
                          defaultValue={task.submission}
                          onBlur={(e) => updateOnboardingTask(task.id, { submission: e.target.value })}
                        />
                        {task.status !== 'Levert' && (
                          <button 
                            onClick={() => handleStatusChange(task.id, 'Levert')}
                            className="px-4 py-2 bg-purple-600 text-white rounded-lg text-xs font-bold uppercase hover:bg-purple-700"
                          >
                            Lever
                          </button>
                        )}
                      </div>
                    )}

                    {/* Manager Approval View */}
                    {(!isCustomer) && (
                      <div className="flex justify-between items-center">
                        <div className="text-sm text-slate-700">
                          <span className="font-bold text-slate-400 text-xs uppercase tracking-widest mr-2">Svar:</span>
                          {task.submission || '(Ingen svar enda)'}
                        </div>
                        <div className="flex gap-2">
                          {task.status === 'Levert' && (
                            <button 
                              onClick={() => handleStatusChange(task.id, 'Godkjent')}
                              className="px-4 py-2 bg-emerald-500 text-white rounded-lg text-xs font-bold uppercase hover:bg-emerald-600 shadow-sm"
                            >
                              Godkjenn
                            </button>
                          )}
                          {!isCustomer && task.owner === 'Team' && task.status !== 'Godkjent' && (
                             <button 
                             onClick={() => handleStatusChange(task.id, 'Godkjent')}
                             className="px-4 py-2 bg-blue-600 text-white rounded-lg text-xs font-bold uppercase hover:bg-blue-700"
                           >
                             Marker som utført
                           </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-16 bg-slate-50 rounded-[2rem] border-2 border-dashed border-slate-200">
          <SafeIcon icon="📋" className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500 font-medium">Ingen oppgaver definert enda.</p>
        </div>
      )}
    </div>
  );
};

export default OnboardingManager;